import React, { useEffect, useState, useRef, useCallback } from "react";
import { Container, Spinner } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { errorLog } from "utils/logger";
import {
  getMaxQuantityDetailsList,
  setMaxQuantityDetailsSearchText,
  setStartPageMaxQuantityDetails,
  exportMaxQuantityDetailsList,
} from "Store/Reducer/maxQuantityDetails.slice";
import {
  setFocusState,
  getAccountCommonList,
  getSegmentOptionList,
} from "Store/Reducer/common.slice";
import _ from "lodash";
import Select from "react-select";
import { getSessionDataOnlyRead } from "utils/common";
import { api } from "utils/api";
import useMobileViewport from "hooks/useMobileViewport";
import useAppLanguage from "hooks/useAppLanguage";
import MobileRecordCard from "components/mobile/MobileRecordCard";
import MobileRecordList from "components/mobile/MobileRecordList";

const theme = {
  gold: "#1A7A70",
  goldLight: "#2f9e93",
  goldBg: "#f4fbfa",
  headerBg: "#1A7A70",
  headerText: "#ffffff",
  rowEven: "#ffffff",
  rowOdd: "#f6fbfb",
  rowHover: "#e8f6f4",
  border: "#b7dfdb",
  text: "#003c38",
  inputBorder: "#1A7A70",
  btnBg: "#1A7A70",
  btnHover: "#14655d",
};

const tableHeaderStyle = {
  background: theme.headerBg,
  color: theme.headerText,
  padding: "10px 12px",
  fontWeight: 700,
  fontSize: "11px",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  borderRight: `1px solid rgba(255,255,255,0.15)`,
  userSelect: "none",
};

const tdStyle = {
  padding: "8px 12px",
  fontSize: "13px",
  color: theme.text,
  borderBottom: `1px solid ${theme.border}`,
  borderRight: `1px solid #ecdcb8`,
  whiteSpace: "nowrap",
};

function TableMaxQuantityDetail({ selectedUser, selectedSegment }) {
  const { t } = useAppLanguage();
  const isMobileViewport = useMobileViewport();
  const {
    maxQuantityDetailsList,
    maxQuantityDetailsLoading,
    maxQuantityDetailsSearchText,
    startPageMaxQuantityDetails,
    totalRecordMaxQuantityDetails,
  } = useSelector(({ maxQuantityDetails }) => maxQuantityDetails);
  const dispatch = useDispatch();
  const containerRef = useRef(null);

  const fetchMore = useCallback(() => {
    if (maxQuantityDetailsLoading) return;
    if (maxQuantityDetailsList.length >= totalRecordMaxQuantityDetails) return;
    dispatch(setStartPageMaxQuantityDetails(startPageMaxQuantityDetails + 1));
    dispatch(
      getMaxQuantityDetailsList({
        segmentId: selectedSegment ? Number(selectedSegment.value) : "",
        userId: selectedUser ? selectedUser.value : "",
        pageNo: startPageMaxQuantityDetails + 1,
        pageSize: 30000,
        searchText: maxQuantityDetailsSearchText,
        appendData: true,
      })
    );
  }, [dispatch, maxQuantityDetailsLoading, maxQuantityDetailsList.length, totalRecordMaxQuantityDetails, startPageMaxQuantityDetails, selectedSegment, selectedUser, maxQuantityDetailsSearchText]);

  const onScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (nearBottom) fetchMore();
  }, [fetchMore]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const rows = maxQuantityDetailsList || [];
  const columns = [
    { Header: t("client"), accessor: "userName" },
    { Header: t("authority"), accessor: "authorityName" },
    { Header: t("market"), accessor: "segment" },
    { Header: t("script"), accessor: "symbol" },
    { Header: t("rateStart"), accessor: "rateStart" },
    { Header: t("rateEnd"), accessor: "rateEnd" },
    { Header: t("positionLimit"), accessor: "positionLimit" },
    { Header: t("type"), accessor: "settingTypeName" },
    { Header: t("maxOrder"), accessor: "maxOrder" },
    { Header: t("minOrder"), accessor: "minOrder" },
  ];

  const formatCell = (row, accessor) => {
    if (accessor === "segment" && row[accessor]) return row[accessor].toUpperCase();
    if (accessor === "symbol") {
      if (row.symbol === null) {
        return row.segment ? `${row.segment.toUpperCase()} SEG` : "-";
      }
      return row.symbol.toUpperCase();
    }
    return row[accessor];
  };

  const getScriptLabel = (row) => formatCell(row, "symbol");

  if (isMobileViewport) {
    return (
      <div className="fx-mqx-table-wrap fx-module--mobile-cards">
        <MobileRecordList
          scrollRef={containerRef}
          onScroll={onScroll}
          style={{ maxHeight: "520px", overflowY: "auto" }}
          loading={maxQuantityDetailsLoading && rows.length === 0}
          loadingText={t("loading")}
          empty={!maxQuantityDetailsLoading && rows.length === 0}
          emptyText={t("dataNotFound")}
        >
          {rows.map((row, i) => (
            <MobileRecordCard
              key={i}
              title={getScriptLabel(row)}
              subtitle={[row.userName, row.segment ? row.segment.toUpperCase() : null]
                .filter(Boolean)
                .join(" · ")}
              fields={columns
                .filter((col) => !["symbol", "userName", "segment"].includes(col.accessor))
                .map((col) => ({
                  label: col.Header,
                  value: formatCell(row, col.accessor),
                }))}
            />
          ))}
        </MobileRecordList>
      </div>
    );
  }

  return (
    <div className="fx-mqx-table-wrap">
      <div
        ref={containerRef}
        className="fx-mqx-table-scroll"
        style={{ maxHeight: "520px", overflowY: "auto", overflowX: "auto" }}
      >
        <table className="fx-mqx-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.accessor} data-mqx-col={col.accessor} style={tableHeaderStyle}>
                  {col.Header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                style={{
                  background: i % 2 === 0 ? theme.rowEven : theme.rowOdd,
                  transition: "background 0.15s",
                }}
              >
                {columns.map((col) => (
                  <td key={col.accessor} data-mqx-col={col.accessor} style={tdStyle}>
                    {formatCell(row, col.accessor)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {maxQuantityDetailsLoading && (
          <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
            <Spinner color="warning" />
          </div>
        )}
        {!maxQuantityDetailsLoading && rows.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "32px",
              color: theme.gold,
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            {t("dataNotFound")}
          </div>
        )}
      </div>
    </div>
  );
}

function MaxQuantityDetails({ darkTheme = false }) {
  const dispatch = useDispatch();
  const { t } = useAppLanguage();
  const {
  maxQuantityDetailsList,
  maxQuantityDetailsSearchText,
  totalRecordMaxQuantityDetails
} = useSelector(({ maxQuantityDetails }) => maxQuantityDetails || {});
  const { focusState, segmentsList, accountOptionList } = useSelector(
    ({ common }) => common || {}
  );
  const { userRole = "User" } = useSelector(({ auth }) => auth || {});

  const localData = getSessionDataOnlyRead() || {};
  const defaultUserCode =
    localData.userName ||
    localData.UserName ||
    localData.userCode ||
    localData.UserCode ||
    "14251710";
  const userName = defaultUserCode;

  const [selectedUser, setSelectedUser] = useState("");
  const [selectedSegment, setSelectedSegment] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [exportingPdf, setExportingPdf] = useState(false);

  const ui = darkTheme
    ? {
        gold: "#2f9e93",
        goldLight: "#5ec4ba",
        goldBg: "transparent",
        text: "#e8e4df",
        inputBorder: "#3a3a3a",
        inputBg: "#1a1a1a",
        btnBg: "#1A7A70",
        btnHover: "#14655d",
      }
    : {
        gold: theme.gold,
        goldLight: theme.goldLight,
        goldBg: theme.goldBg,
        text: theme.text,
        inputBorder: theme.inputBorder,
        inputBg: "#fffdf7",
        btnBg: theme.btnBg,
        btnHover: theme.btnHover,
      };

  useEffect(() => {
    dispatch(getSegmentOptionList());
    dispatch(getAccountCommonList({ roleName: "User" }));
    // preselect first client for non-User roles when list arrives
  }, [dispatch]);

  useEffect(() => {
    if (userRole !== "User" && accountOptionList.length > 0 && !selectedUser) {
      setSelectedUser(accountOptionList[0]);
    }
    return () => {
      dispatch(setStartPageMaxQuantityDetails(1));
      dispatch(setFocusState(false));
    };
  }, [dispatch, userRole, accountOptionList, selectedUser]);

  // Auto-select first segment so exports work without manual pick
  useEffect(() => {
    if (segmentsList.length > 0 && !selectedSegment) {
      setSelectedSegment(segmentsList[0]);
    }
  }, [segmentsList, selectedSegment]);

  const handlePdfExport = () => {
  try {
    if (!maxQuantityDetailsList || maxQuantityDetailsList.length === 0) {
      alert(t("noDataToExport"));
      return;
    }

    const doc = new jsPDF("l", "pt", "a4");

    const columns = [
      t("client"),
      t("authority"),
      t("market"),
      t("script"),
      t("rateStart"),
      t("rateEnd"),
      t("positionLimit"),
      t("type"),
      t("maxOrder"),
      t("minOrder"),
    ];

    const body = maxQuantityDetailsList.map((item) => [
      item.userName || "-",
      item.authorityName || "-",
      item.segment ? item.segment.toUpperCase() : "-",
      item.symbol === null
        ? item.segment
          ? `${item.segment.toUpperCase()} SEG`
          : "-"
        : item.symbol?.toUpperCase(),
      item.rateStart || "-",
      item.rateEnd || "-",
      item.positionLimit || "-",
      item.settingTypeName || "-",
      item.maxOrder || "-",
      item.minOrder || "-",
    ]);

    doc.text(`FxBazzar - ${t("maxqty")}`, 40, 30);

    autoTable(doc, {
      head: [columns],
      body,
      startY: 50,
      styles: { fontSize: 9 },
    });

    doc.save("Max_Quantity_Details.pdf");
  } catch (error) {
    errorLog("PDF export failed", error);
  }
};

  const debounceSearch = React.useCallback(
    _.debounce((val) => {
      dispatch(setMaxQuantityDetailsSearchText(val));
      dispatch(setStartPageMaxQuantityDetails(1));
      dispatch(
        getMaxQuantityDetailsList({
          segmentId: selectedSegment ? Number(selectedSegment.value) : "",
          userId: selectedUser ? selectedUser.value : "",
          searchText: val,
          pageNo: 1,
          pageSize: 30000,
          appendData: false,
        })
      );
    }, 800),
    [selectedSegment, selectedUser]
  );

  const labelStyle = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: ui.gold,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 12px",
    border: `1px solid ${ui.inputBorder}`,
    borderRadius: 4,
    fontSize: 13,
    color: ui.text,
    background: ui.inputBg,
    outline: "none",
  };

  const btnStyle = {
    background: ui.btnBg,
    color: "#fff",
    border: "none",
    borderRadius: 4,
    padding: "9px 20px",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
    letterSpacing: "0.03em",
    transition: "background 0.2s",
    whiteSpace: "nowrap",
  };

  return (
    <>
      <Container
  fluid
  className={`fx-mqx-page${darkTheme ? " fx-mqx-page--dark" : ""}`}
  style={{
    paddingTop: 24,
    paddingBottom: 24,
    paddingLeft: 40,
    background: ui.goldBg,
    minHeight: "100%",
    color: ui.text,
  }}
>
        <div className="fx-mqx-form">
          <div className="fx-mqx-field">
            <label className="fx-mqx-label" style={labelStyle}>{t("client")}</label>
            {userRole === "User" ? (
              <input className="fx-mqx-input" style={inputStyle} value={userName} disabled />
            ) : (
              <Select
                classNamePrefix="fx-mqx-select"
                options={accountOptionList}
                placeholder={t("client")}
                value={selectedUser}
                onChange={(opt) => setSelectedUser(opt)}
              />
            )}
          </div>

          <div className="fx-mqx-field">
            <label className="fx-mqx-label" style={labelStyle}>
              {t("segment")} <span style={{ color: "red" }}>*</span>
            </label>
            <Select
              classNamePrefix="fx-mqx-select"
              options={segmentsList}
              placeholder={t("segment")}
              value={selectedSegment}
              onChange={(opt) => setSelectedSegment(opt)}
            />
          </div>

          <div className="fx-mqx-field fx-mqx-field--search">
            <label className="fx-mqx-label" style={labelStyle}>{t("search")}</label>
            <div className={`fx-mqx-search${focusState ? " is-focused" : ""}`}>
              <span className="fx-mqx-search__icon" aria-hidden>🔍</span>
              <input
                className="fx-mqx-search__input"
                placeholder={t("searchScriptMarket")}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 13,
                  color: ui.text,
                  width: "100%",
                  minWidth: 0,
                }}
                value={searchValue}
                onFocus={() => dispatch(setFocusState(true))}
                onBlur={() => dispatch(setFocusState(false))}
                onChange={(e) => {
                  setSearchValue(e.target.value);
                  debounceSearch(e.target.value);
                }}
              />
            </div>
          </div>

          <div className="fx-mqx-submit">
            <button
              type="button"
              className="fx-mqx-btn fx-mqx-btn--primary"
              style={btnStyle}
              onMouseEnter={(e) => (e.currentTarget.style.background = ui.btnHover)}
              onMouseLeave={(e) => (e.currentTarget.style.background = ui.btnBg)}
              onClick={() => {
                if (!selectedSegment) {
                  alert(t("selectSegmentFirst"));
                  return;
                }
                dispatch(
                  getMaxQuantityDetailsList({
                    segmentId: Number(selectedSegment.value),
                    userId: selectedUser ? selectedUser.value : "",
                    searchText: maxQuantityDetailsSearchText,
                    pageNo: 1,
                    pageSize: 30000,
                    appendData: false,
                  })
                );
              }}
            >
              {t("getMaxQtyDetails")}
            </button>
          </div>

          <div className="fx-mqx-actions">
            <div className="fx-mqx-btn-row">
              <button
                type="button"
                className="fx-mqx-btn fx-mqx-btn--pdf"
                style={{ ...btnStyle, padding: "8px 12px", background: "#e95c5c" }}
                disabled={exportingPdf}
                onClick={handlePdfExport}
              >
                {exportingPdf ? t("generating") : t("pdf")}
              </button>
              <button
                type="button"
                className="fx-mqx-btn fx-mqx-btn--excel"
                style={{ ...btnStyle, padding: "8px 12px", background: "#2f9e44" }}
                onClick={() => {
                  if (!selectedSegment) {
                    alert(t("selectSegmentExcel"));
                    return;
                  }
                  dispatch(
                    exportMaxQuantityDetailsList({
                      exportType: "excel",
                      segmentId: Number(selectedSegment.value),
                      userId: selectedUser ? selectedUser.value : "",
                      searchText: maxQuantityDetailsSearchText,
                    })
                  );
                }}
              >
                {t("excel")}
              </button>
            </div>
            {totalRecordMaxQuantityDetails > 0 && (
              <div className="fx-mqx-total" style={{ fontSize: 15, fontWeight: 700, color: ui.text }}>
                {t("totalRecords")}: <span style={{ color: ui.goldLight, fontSize: 16 }}>{totalRecordMaxQuantityDetails}</span>
              </div>
            )}
          </div>
        </div>

        <TableMaxQuantityDetail selectedUser={selectedUser} selectedSegment={selectedSegment} />
      </Container>
    </>
  );
}

export default MaxQuantityDetails;
